// MapView.jsx - Uses categoryName for marker colors
import { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { getMapProperties } from './api.js';
import CopyFailureAlert from '../Alertpage/CopyAlert/CopyFailureAlert';
import CopySuccessAlert from '../Alertpage/CopyAlert/CopySuccessAlert';
import {GOOGLEMAPS_API_KEY} from '../../Api/ApiUrls.js';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const PRICE_TAG_ZOOM = 13;
const CLUSTER_LABEL_MAX_ZOOM = 8; 

const _stateCache = new Map();
const _pendingRequests = new Map();

const GOOGLE_API_KEY = GOOGLEMAPS_API_KEY; 

const getStateName = async (lat, lng) => {
  const key = `${parseFloat(lat).toFixed(2)},${parseFloat(lng).toFixed(2)}`;

  if (_stateCache.has(key)) return _stateCache.get(key);
  if (_pendingRequests.has(key)) return _pendingRequests.get(key);

  const promise = (async () => {
    try {
      const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&result_type=administrative_area_level_1&key=${GOOGLE_API_KEY}`;

      const res = await fetch(url);
      if (!res.ok) throw new Error(`Google Geocoding error: ${res.status}`);

      const data = await res.json();

      if (data.status !== 'OK' || !data.results?.length) {
        throw new Error(`No results: ${data.status}`);
      }

      // Extract the state name from address_components
      const components = data.results[0].address_components;
      const stateComponent = components.find(c =>
        c.types.includes('administrative_area_level_1')
      );

      const name = stateComponent?.long_name || 'India';
      _stateCache.set(key, name);
      return name;

    } catch (err) {
      console.warn('Google reverse geocode failed:', err);
      const fallback = 'India';
      _stateCache.set(key, fallback);
      return fallback;
    } finally {
      _pendingRequests.delete(key);
    }
  })();

  _pendingRequests.set(key, promise);
  return promise;
};

// ── cluster state label — white card to the RIGHT of the cluster bubble ──────
const buildClusterLabelIcon = (text, loading = false) => L.divIcon({
  className: 'csl-wrap',
  html: `
    <div style="
      display:flex;align-items:center;
      pointer-events:none;
      transform:translateY(-50%);
    ">
      <!-- thin connector line -->
      <div style="width:14px;height:2px;background:#1565C0;flex-shrink:0;"></div>
      <!-- label card -->
      <div style="
        background:#fff;
        border-radius:10px;
        padding:4px 10px;
        box-shadow:0 3px 10px rgba(0,0,0,0.22);
        border-left:3px solid #1565C0;
        font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;
        font-size:11px;font-weight:700;color:#1a1a1a;
        white-space:nowrap;
        ${loading ? 'opacity:.55;' : ''}
      ">
        ${loading ? '⏳ …' : `${text}`}
      </div>
    </div>`,
  iconSize: [180, 22],
  iconAnchor: [0, 11],   // left-middle → label floats right of the cluster dot
});

const buildSimplePriceTagIcon = (price, isSelected = false, categoryColor = '#1a73e8') => {
const formatPriceForTag = (price) => {
  const num = Number(price);
  if (num >= 10000000) {
    return `${(num / 10000000).toFixed(2)}Cr`;
  } else if (num >= 100000) {
    return `${(num / 100000).toFixed(2)}L`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(2)}K`;
  }
  return `${num.toLocaleString('en-IN')}`;
};
  
  const formattedPrice = formatPriceForTag(price);
  const size = isSelected ? 40 : 32;
  
  // Adjust font size based on text length
  const getFontSize = (text) => {
    if (text.length > 12) return '9px';
    if (text.length > 8) return '10px';
    return '11px';
  };
  
  return L.divIcon({
    className: 'custom-price-tag',
    html: `<div style="
      background-color: ${isSelected ? '#FFD600' : categoryColor};
      color: ${isSelected ? '#000' : '#fff'};
      padding: 4px 8px; 
      border-radius: 20px;
      font-size: ${getFontSize(formattedPrice)}; 
      font-weight: bold;
      white-space: nowrap; 
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
      border: ${isSelected ? '2px solid #000' : 'none'};
      font-family: Arial, sans-serif; 
      text-align: center; 
      min-width: 50px;
    ">${formattedPrice}</div>`,
    iconSize: [size, 20],
    popupAnchor: [0, -10]
  });
};
const getMarkerIcon = (categoryName, isSelected = false, categoryColorMap = {}) => {
  const color = categoryColorMap[categoryName] || '#E53935';

  if (isSelected) {
    return L.divIcon({
      html: `<div style="
        background-color: ${color};
        width: 28px; 
        height: 28px;
        border-radius: 50%;
        border: 4px solid #FFD600;
        display: flex; 
        align-items: center; 
        justify-content: center;
        box-shadow: 0 0 20px rgba(255,214,0,0.6);
      ">
        <div style="
          width: 10px;
          height: 10px;
          background-color: white;
          border-radius: 50%;
        "></div>
      </div>`,
      className: 'custom-marker-selected',
      iconSize: [28, 28],
      popupAnchor: [0, -14]
    });
  }

  return L.divIcon({
    html: `<div style="
      background-color: ${color};
      width: 18px; 
      height: 18px;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
    "></div>`,
    className: 'custom-marker',
    iconSize: [18, 18],
    popupAnchor: [0, -9]
  });
};

const MapView = ({
  onMapReady,
  onViewportChange,
  onPropertiesUpdate,
  filters,
  selectedId,
  onMarkerClick,
  flyTarget,
  categoryColorMap = {},
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const clusterGroupRef = useRef(null);
  const markerByIdRef = useRef(new Map());
  const debounceRef = useRef(null);
  const [properties, setProperties] = useState([]);
  const [mapReady, setMapReady] = useState(false);
  const [isMapInitializing, setIsMapInitializing] = useState(true);
  const [currentZoom, setCurrentZoom] = useState(5);
  const isFlyingRef = useRef(false);
  const [showInitialSpinner, setShowInitialSpinner] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
const [showNoPropsMsg, setShowNoPropsMsg] = useState(false);
const [noPropsReason, setNoPropsReason] = useState(null);
  const filtersRef = useRef(filters);

  const clusterLabelLayerRef = useRef(null);
  const clusterLabelMapRef = useRef(new Map());

  const showAlert = (type, message) => {
    if (type === 'success') {
      setAlertMessage(message); setShowSuccess(true);
      setTimeout(() => { setShowSuccess(false); setAlertMessage(''); }, 3000);
    } else if (type === 'failure') {
      setAlertMessage(message); setShowFailure(true);
      setTimeout(() => { setShowFailure(false); setAlertMessage(''); }, 3000);
    }
  };

  const fetchProperties = useCallback(async (viewport) => {
    if (!viewport || !mapRef.current) return;
    try {
      const data = await getMapProperties({
        bounds: viewport.bounds, zoom: viewport.zoom,
        propertyType: filters.propertyType,
        priceMin: filters.priceMin, priceMax: filters.priceMax,
      });
      if (data.success && data.properties) {
        setProperties(data.properties);
        onPropertiesUpdate?.(data.properties, showAlert);
        setShowNoPropsMsg(data.properties.length === 0);
      } else {
        onPropertiesUpdate?.([]);
        setShowNoPropsMsg(true);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      onPropertiesUpdate?.([]);
      setShowNoPropsMsg(true);
    }
  }, [filters, onPropertiesUpdate]);

  const getCurrentViewport = useCallback(() => {
    if (!mapRef.current) return null;
    try {
      const map = mapRef.current;
      if (!map.getBounds) return null;
      const bounds = map.getBounds();
      if (!bounds) return null;
      return {
        bounds: {
          south: bounds.getSouth(), north: bounds.getNorth(),
          west: bounds.getWest(), east: bounds.getEast()
        },
        zoom: map.getZoom(),
      };
    } catch (error) {
      console.error('Error getting viewport:', error);
      return null;
    }
  }, []);

  // ── NEW: scan visible cluster bubbles and draw a state label next to each ──
  const refreshClusterLabels = useCallback(() => {
    const map = mapRef.current;
    const layer = clusterLabelLayerRef.current;
    if (!map || !layer) return;

    const zoom = map.getZoom();

    // hide all labels when zoomed in past threshold
    if (zoom >= CLUSTER_LABEL_MAX_ZOOM) {
      layer.clearLayers();
      clusterLabelMapRef.current.clear();
      return;
    }

    // collect currently visible cluster markers from the map
    const visibleClusters = [];
    map.eachLayer(l => {
      if (l instanceof L.MarkerCluster) {
        visibleClusters.push(l);
      }
    });

    if (!visibleClusters.length) return;

    // clear old labels
    layer.clearLayers();
    clusterLabelMapRef.current.clear();

    visibleClusters.forEach(cluster => {
      const { lat, lng } = cluster.getLatLng();
      const key = `${lat.toFixed(4)},${lng.toFixed(4)}`;

      // place loading label immediately
      const loadingMarker = L.marker([lat, lng], {
        icon: buildClusterLabelIcon('', true),
        interactive: false,
        zIndexOffset: 9999,
      });
      layer.addLayer(loadingMarker);
      clusterLabelMapRef.current.set(key, loadingMarker);

      getStateName(lat, lng).then(name => {
        const existing = clusterLabelMapRef.current.get(key);
        if (!existing) return; 
        existing.setIcon(buildClusterLabelIcon(name, false));
      });
    });
  }, []);

  // ✅ CHANGE 2: Keep filtersRef updated with latest filters
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Initialize map
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;

    console.log('Initializing map...');
    setIsMapInitializing(true);

const savedMapState = JSON.parse(localStorage.getItem('rentmapState') || 'null');

const map = L.map(containerRef.current, {
  center: savedMapState ? [savedMapState.lat, savedMapState.lng] : [20.5937, 78.9629],
  zoom: savedMapState ? savedMapState.zoom : 5,
  minZoom: 4,
  maxZoom: 18,
  zoomControl: true,
});
    mapRef.current = map;

    const style = document.createElement('style');
    style.textContent = `.leaflet-top { top: 140px !important; }`;
    document.head.appendChild(style);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19, minZoom: 4,
    }).addTo(map);

    // ── NEW: label layer sits above everything ────────────────────────────
    clusterLabelLayerRef.current = L.layerGroup().addTo(map);

    const clusterGroup = L.markerClusterGroup({
      chunkedLoading: true,
      disableClusteringAtZoom: 14,
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: false,
      showCoverageOnHover: false,
iconCreateFunction: (cluster) => {
  const firstChild = cluster.getAllChildMarkers()[0];
  const categoryName = firstChild?.options?.categoryName || '';
  const color = categoryColorMap[categoryName] || '#1976D2';

  const zoom = cluster._group._map.getZoom();
  const size = zoom >= 10 ? 20 : 20;

  return L.divIcon({
    html: `<div style="
      background-color: ${color};
      border-radius: 50%;
      width: ${size}px;
      height: ${size}px;
      border: 2px solid white;
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
      display:flex;
      align-items:center;
      justify-content:center;
      color:white;
      font-size:10px;
      font-weight:bold;
    ">${zoom >= 10 ? cluster.getChildCount() : ''}</div>`,
    className: 'custom-cluster-icon',
    iconSize: [size, size]
  });
}
    });
    clusterGroupRef.current = clusterGroup;
    map.addLayer(clusterGroup);

    const updateZoom = () => {
      if (mapRef.current) setCurrentZoom(mapRef.current.getZoom());
    };
    map.on('zoomend', updateZoom);

    // ── NEW: after every cluster animation → refresh labels ──────────────
    clusterGroup.on('animationend', () => {
      // small delay so cluster DOM elements are fully placed
      setTimeout(() => refreshClusterLabels(), 120);
    });

    // ── NEW: also refresh on moveend (pan) ────────────────────────────────
    map.on('moveend', () => {
      if (map.getZoom() < CLUSTER_LABEL_MAX_ZOOM) {
        setTimeout(() => refreshClusterLabels(), 120);
      }
    });

    map.whenReady(() => {
      console.log('Map is fully ready');
      setIsMapInitializing(false);
      setMapReady(true);
      onMapReady?.(map);
      updateZoom();

      const viewport = getCurrentViewport();
      if (viewport) fetchProperties(viewport);
      setTimeout(() => setShowInitialSpinner(false), 5000);
    });
// Find the handleMoveEnd function (around line 220) and update it:

const handleMoveEnd = () => {
  if (!mapRef.current) return;
  const viewport = getCurrentViewport();
  if (!viewport) return;
  onViewportChange?.(viewport);

  const center = mapRef.current.getCenter();
  localStorage.setItem('rentstate', JSON.stringify({
    zoom: mapRef.current.getZoom(),
    lat: center.lat,
    lng: center.lng,
  }));

  
  if (debounceRef.current) clearTimeout(debounceRef.current);
  debounceRef.current = setTimeout(() => {
    if (mapRef.current && !isFlyingRef.current) {
      const currentViewport = getCurrentViewport();
      if (currentViewport) {
        (async () => {
          try {
            const data = await getMapProperties({
              bounds: currentViewport.bounds,
              zoom: currentViewport.zoom,
              propertyType: filtersRef.current.propertyType,  
              priceMin: filtersRef.current.priceMin,        
              priceMax: filtersRef.current.priceMax,         
            });
            
            if (data.success && data.properties) {
              setProperties(data.properties);
              onPropertiesUpdate?.(data.properties, showAlert);
              setShowNoPropsMsg(data.properties.length === 0);
            } else {
              onPropertiesUpdate?.([]);
              setShowNoPropsMsg(true);
            }
          } catch (error) {
            console.error('Error fetching properties on zoom:', error);
            onPropertiesUpdate?.([]);
            setShowNoPropsMsg(true);
          }
        })();
      }
    }
  }, 500);
};
    return () => {
      console.log('Cleaning up map...');
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (clusterLabelLayerRef.current) {
        try { clusterLabelLayerRef.current.clearLayers(); } catch (e) {}
      }
      if (clusterGroupRef.current) {
        try { clusterGroupRef.current.clearLayers(); } catch (e) { console.warn(e); }
      }
      if (mapRef.current) {
        try { mapRef.current.off(); mapRef.current.remove(); } catch (e) { console.warn(e); }
        mapRef.current = null;
      }
      setMapReady(false);
      setIsMapInitializing(true);
      setShowInitialSpinner(true);
    };
  }, [refreshClusterLabels]);

  // Refetch when filters change
  useEffect(() => {
    if (!mapReady || isMapInitializing || !mapRef.current) return;
    console.log('Filters changed, refetching with:', filters);
    const viewport = getCurrentViewport();
    if (!viewport) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    fetchProperties(viewport);
  }, [filters, mapReady, isMapInitializing, getCurrentViewport, fetchProperties]);

  // ✅ Update markers — use property.categoryName for color lookup
  useEffect(() => {
    if (!mapReady || !clusterGroupRef.current || isMapInitializing) return;

    try {
      const cluster = clusterGroupRef.current;
      if (!cluster) return;

      cluster.clearLayers();
      markerByIdRef.current.clear();

      // clear cluster labels when data refreshes
      if (clusterLabelLayerRef.current) clusterLabelLayerRef.current.clearLayers();
      clusterLabelMapRef.current.clear();

      if (!properties.length) return;

      const markers = [];
      const usePriceTags = currentZoom >= PRICE_TAG_ZOOM;

      properties.forEach(property => {
        const lat = parseFloat(property.lat);
        const lng = parseFloat(property.lng);

        if (isNaN(lat) || isNaN(lng)) {
          console.warn("Skipping invalid property:", property);
          return;
        }

        const isSelected = selectedId === property.id;
        let icon;

        if (usePriceTags && property.price) {
          const categoryColor = categoryColorMap[property.categoryName] || '#1a73e8';
          icon = buildSimplePriceTagIcon(property.price, isSelected, categoryColor);
        } else {
          icon = getMarkerIcon(property.categoryName, isSelected, categoryColorMap);
        }


// AFTER
const marker = L.marker([lat, lng], { icon, categoryName: property.categoryName });
        marker.on('click', () => onMarkerClick(property));
        markerByIdRef.current.set(property.id, marker);
        markers.push(marker);
      });

      cluster.addLayers(markers);

      setTimeout(() => refreshClusterLabels(), 400);

    } catch (error) {
      console.error('Error updating markers:', error);
    }
  }, [properties, selectedId, mapReady, isMapInitializing, onMarkerClick, currentZoom, categoryColorMap, refreshClusterLabels]);
// Find the flyTarget useEffect (around line 350-380) and replace with this:

// Fly to target - with smooth panning instead of zooming
useEffect(() => {
  if (!mapReady || !flyTarget || !mapRef.current || isMapInitializing) return;
  
  const lat = parseFloat(flyTarget.lat);
  const lng = parseFloat(flyTarget.lng);
  const targetZoom = flyTarget.zoom;
  
  if (isNaN(lat) || isNaN(lng)) { 
    console.error("Invalid flyTarget:", flyTarget); 
    return; 
  }
  
  isFlyingRef.current = true;
  
  const map = mapRef.current;
  const currentZoom = map.getZoom();
  
  // If zoom level is same or very close, just pan smoothly
  if (Math.abs(currentZoom - targetZoom) < 0.5) {
    map.panTo([lat, lng], {
      duration: 0.8,
      easeLinearity: 0.25
    });
  } else {
    map.flyTo([lat, lng], targetZoom, { 
      duration: 1.0,
      easeLinearity: 0.5
    });
  }
  
  const resetAndFetch = () => {
    isFlyingRef.current = false;
    map.off('moveend', resetAndFetch);
    
    const viewport = getCurrentViewport();
    if (viewport) {
      fetchProperties(viewport);
    }
  };
  
  map.once('moveend', resetAndFetch);
  
  // Safety timeout
  setTimeout(() => { 
    isFlyingRef.current = false; 
  }, 2000);
  
}, [flyTarget, mapReady, isMapInitializing, getCurrentViewport, fetchProperties]);
  return (
    <>
      <div
        ref={containerRef}
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 0,
          backgroundColor: 'transparent'
        }}
      />

      {showInitialSpinner && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(2px)'
        }}>
          <div style={{
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            borderRadius: '12px', padding: '24px 32px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <div style={{
              width: '48px', height: '48px',
              border: '4px solid rgba(255, 255, 255, 0.3)',
              borderTopColor: '#fff', borderRightColor: '#fff',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite'
            }} />
            <div style={{ color: '#fff', fontSize: '16px', fontWeight: '500', fontFamily: 'Arial, sans-serif' }}>
              Loading properties...
            </div>
          </div>
        </div>
      )}

      {showSuccess && (
        <CopySuccessAlert message={alertMessage} onClose={() => { setShowSuccess(false); setAlertMessage(""); }} />
      )}
      {showFailure && (
        <CopyFailureAlert message={alertMessage} onClose={() => { setShowFailure(false); setAlertMessage(""); }} />
      )}



{showNoPropsMsg && !showInitialSpinner && (
  <div style={{
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1001,
    backgroundColor: 'rgba(0,0,0,0.85)',
    color: 'white',
    padding: '16px 24px',
    borderRadius: '12px',
    textAlign: 'center',
    maxWidth: '280px',
    backdropFilter: 'blur(8px)',
    pointerEvents: 'none'
  }}>
    <div style={{ fontSize: '48px', marginBottom: '8px' }}>
      {filters.priceMin > 0 || filters.priceMax < 1000000000 ? '💰' : 
       filters.propertyType?.length > 0 ? '🏷️' : '🔍'}
    </div>
    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
      {filters.priceMin > 0 || filters.priceMax < 1000000000 ? 'No Properties in Price Range' :
       filters.propertyType?.length > 0 ? 'No Properties in Category' :
       'No Properties in Area'}
    </div>
    <div style={{ fontSize: '13px', opacity: 0.9 }}>
      {filters.priceMin > 0 || filters.priceMax < 1000000000 ? 
       `No properties found between ₹${filters.priceMin?.toLocaleString()} - ₹${filters.priceMax?.toLocaleString()}. Try adjusting price range.` :
       filters.propertyType?.length > 0 ? 
       `No properties found for selected categories. Try different categories.` :
       'No properties available in this zoom area. Try zooming out to see more results.'}
    </div>
  </div>
)}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .csl-wrap { background: transparent !important; border: none !important; box-shadow: none !important; }
      `}</style>
    </>
  );
};

export default MapView;